import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircleIcon, MailIcon } from '@utils/icons';
import { Button } from '@components/ui/Button/Button';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import { useHelpGuides, useHelpSearchIndex } from './useHelp';
import { searchGuides } from './searchGuides';
import { buildHelpTree, gridOrder } from './helpTree';
import { GuideCard } from './GuideCard';
import { ParentGuideCard } from './ParentGuideCard';
import { SearchResults } from './SearchResults';
import { HelpSettingsMenu } from './HelpSettingsMenu';
import styles from './HelpCenterPage.module.css';

export function HelpCenterPage() {
  const { t } = useTranslation();
  const { guides, isLoading, isError } = useHelpGuides();
  const [query, setQuery] = useState('');
  const searchTerm = query.trim();
  const { index, isPending: isIndexPending, isError: isSearchError } = useHelpSearchIndex();
  const hits = useMemo(() => searchGuides(index, searchTerm), [index, searchTerm]);
  const tree = useMemo(() => buildHelpTree(guides), [guides]);
  const order = useMemo(() => gridOrder(tree), [tree]);
  const bySlug = useMemo(() => new Map(guides.map((guide) => [guide.slug, guide])), [guides]);

  return (
    <div className={styles.hub}>
      <section className={styles.hero}>
        <span className={styles.heroIcon}><HelpCircleIcon size={44} /></span>
        <div className={styles.heroBody}>
          <SearchInput
            className={styles.search}
            value={query}
            onChange={setQuery}
            placeholder={t('help.searchPlaceholder')}
            ariaLabel={t('help.searchPlaceholder')}
            ariaLabelClear={t('help.clearSearch')}
          />
        </div>
        <HelpSettingsMenu />
      </section>

      {/* Searching is local, so results follow every keystroke in the same frame. Only
          before the index first arrives is there nothing to search: the grid stays up
          rather than blanking. */}
      {searchTerm !== '' && !isIndexPending ? (
        <SearchResults query={searchTerm} hits={hits} isError={isSearchError} bySlug={bySlug} />
      ) : isLoading ? null : isError ? (
        <DataUnavailable message={t('help.loadFailed')} />
      ) : guides.length === 0 ? (
        <DataUnavailable message={t('help.empty')} />
      ) : (
        <section>
          <div className={styles.sectionHead}>
            <h2>{t('help.pageGuides')}</h2>
            <span>{t('help.count', { pages: t('help.pagesCount', { count: tree.length }), guides: t('help.guidesCount', { count: guides.length }) })}</span>
          </div>
          <div className={styles.grid}>
            {tree.map((node) =>
              node.children.length > 0 ? (
                <ParentGuideCard key={node.guide.slug} node={node} order={order.get(node.guide.slug)!} />
              ) : (
                <GuideCard key={node.guide.slug} guide={node.guide} order={order.get(node.guide.slug)!} />
              ),
            )}
          </div>
        </section>
      )}

      <div className={styles.contact}>
        <span className={styles.contactIcon}><MailIcon size={18} /></span>
        <span className={styles.contactText}><b>{t('help.contactTitle')}</b><span>{t('help.contactBody')}</span></span>
        <Button variant="secondary" size="sm" disabled>{t('help.contactButton')}</Button>
      </div>
    </div>
  );
}
